import {
  FieldQueryOptions,
  MAX_FIELD_PAGE_SIZE,
  ProgramEngagementStatus,
  ProgramTaskRole,
  UserId,
} from '@cibo/core'
import { QUERY_KEY as LAND_MANAGER_QUERY_KEY, usePaginatedFields } from '@cibo/landmanager'
import { useAuth } from '@cibo/profile'
import { EngagementRouteParams } from '@cibo/ui'
import { useQueryClient, UseQueryResult } from '@tanstack/react-query'
import { propEq } from 'ramda'
import { createContext, ReactNode, useContext, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { RollbackProvider } from '../../features/ProgramEngagementPlayer/RollbackProvider'
import { TaskDataModel } from '../../features/ProgramEngagementPlayer/models/TaskDataModel'
import {
  PROGRAMS_QUERY_KEY,
  useProgramEngagement,
  useProgramEngagementDetails,
  useProgramModel,
} from '../../queries'
import { useEngagementTaskData } from '../../queries/useEngagementTaskData'

type ProgramEngagementState = EngagementRouteParams & {
  basePath: string
  program: ReturnType<typeof useProgramModel>
  ownerName: string

  taskData: UseQueryResult<TaskDataModel, Error>
  dataEntryDetail(spec: { parentTraitId: string; traitId: string }): any

  engagement: ReturnType<typeof useProgramEngagement>
  /** @deprecated - prefer using `engagement.refresh()` */
  refreshEngagement(): void

  fieldModels: ReturnType<typeof usePaginatedFields>
  baseFieldsQuery?: FieldQueryOptions

  continueFunction: (() => void) | undefined
  setContinueFunction(f: ((args: any) => void) | undefined): void

  userRole: ProgramTaskRole
  userId?: UserId
  programId: string
  stepStatus?: ProgramEngagementStatus
  taskStatus?: ProgramEngagementStatus
  taskEditorParams?: any
}

const DEFAULT_VALUES = {
  basePath: '',
  ownerName: '',
  engagement: {} as ProgramEngagementState['engagement'],
  userRole: 'participant',
  program: {} as ProgramEngagementState['program'],
  taskData: {} as ProgramEngagementState['taskData'],
  continueFunction: undefined,
  setContinueFunction: () => console.log('default setContinueFunction()'),
  refreshEngagement: () => console.log('default refreshEngagement()'),
  refreshFields: () => console.log('default refreshFields()'),
  dataEntryDetail: () => console.log('default dataEntryDetail()'),
  fieldModels: {} as ProgramEngagementState['fieldModels'],
  programId: '',
} as ProgramEngagementState

export const ProgramEngagementContext = createContext<ProgramEngagementState>(DEFAULT_VALUES)
ProgramEngagementContext.displayName = 'ProgramEngagementContext'

type ProgramEngagementProviderProps = {
  basePath: string
  children: ReactNode
  engagementId?: string
  programId: string
}

export const ProgramEngagementProvider = ({
  basePath,
  children,
  programId,
}: ProgramEngagementProviderProps) => {
  const { t } = useTranslation('@cibo/programs/ProgramEngagementProvider')

  const { engagementId, stageId = 'apply', stepId, taskId } = useParams<EngagementRouteParams>()

  const engagement = useProgramEngagement({ id: engagementId })
  const program = useProgramModel({ programId: engagement.data?.programId ?? programId })

  const baseFieldsQuery = { workflow: engagementId, sortField: 'name', sortDir: 'asc' as const }
  const fieldModels = usePaginatedFields(0, MAX_FIELD_PAGE_SIZE, baseFieldsQuery)

  const { userId: selfId } = useAuth()
  const userRole = selfId === engagement.data?.ownedBy?.userId ? 'participant' : 'manager'
  const ownerName = fieldModels.data?.items[0]?.ownerLabel ?? (t('applicant') as string)
  const userId = engagement.data?.ownedBy?.userId

  const details = useProgramEngagementDetails({
    engagementId,
  })

  const taskConfig = program.data?.task(taskId)

  const taskData = useEngagementTaskData(
    {
      engagementId,
      taskId,
    },
    {
      enabled: taskConfig?.rule?.type === 'dataEntry' && !!engagementId && !!taskId,
    }
  )

  const queryClient = useQueryClient()

  const refreshEngagement = () => {
    queryClient.invalidateQueries({
      queryKey: [PROGRAMS_QUERY_KEY.PROGRAM_ENGAGEMENTS, engagementId],
    })
    queryClient.invalidateQueries({
      queryKey: [LAND_MANAGER_QUERY_KEY.FIELDS_PAGINATED],
    })
    queryClient.invalidateQueries({
      queryKey: [PROGRAMS_QUERY_KEY.TASK_DATA],
    })
  }

  const [continueFunction, setContinueFunction] = useState<(() => void) | undefined>()

  const taskStatus = engagement.data?.getTaskStatus(taskId)
  const taskEditorParams = taskStatus?.displays?.find(propEq('type', 'taskEditor'))?.params

  return (
    <ProgramEngagementContext.Provider
      value={{
        engagementId,
        stageId,
        stepId,
        taskId,
        programId,

        basePath,
        dataEntryDetail: ({ parentTraitId, traitId }) => {
          const parentDetail = details.data?.find(({ traitId }) => traitId === parentTraitId)
          // @ts-ignore not sure how to type dataEntry details yet
          return parentDetail?.result?.[traitId]
        },
        taskData,
        program,
        refreshEngagement,
        ownerName,
        engagement,
        userRole,
        userId,

        fieldModels,

        baseFieldsQuery,
        continueFunction,
        setContinueFunction: (f: ((args: any) => void) | undefined) => {
          // useState interprets a function as an updater,
          // but we want the new state to be the function
          setContinueFunction(() => f)
        },
        stepStatus: engagement.data?.getStepStatus(stepId),
        taskStatus,
        taskEditorParams,
      }}
    >
      <RollbackProvider engagementId={engagementId} taskStatus={taskStatus}>
        {children}
      </RollbackProvider>
    </ProgramEngagementContext.Provider>
  )
}

export const useProgramEngagementContext = () => useContext(ProgramEngagementContext)
